import { useContext, useEffect, useState, useMemo } from "react";
import { confirmAlert } from "react-confirm-alert";
import toastr from "toastr";
import AlertModal from "../../../../utils/components/AlertModal";
import Input from "../../../../utils/components/Input";
import Modal from "../../../../utils/components/Modal";
import SelectBox from "../../../../utils/components/SelectBox";
import { useLanguage } from "../../../../utils/hooks/useLanguage";
import { RequestContext } from "../../../../utils/context/RequestContext";
import API from "../../../../utils/API";
import { useProject } from "../../../../utils/hooks/useProject";

import { useOutletContext } from "react-router";

const FormTemplateItem = ({ 
  className,
  id,
  template,
  statusSets,
  formDependencyDetailInfo,
  previousFormDependencyDetailInfo,
  setPreviousFormDependencyDetailInfo 
}) => {
  const [translate] = useOutletContext();
  const [statuses, setStatuses] = useState([]);

  const statusValue = useMemo(() => {
    const foundTemplate = previousFormDependencyDetailInfo.templates.find((tmplt) => tmplt.id === template.id);

    if (foundTemplate?.status_detail_id) {
      return {
        value: foundTemplate.status_detail_id,
        label: foundTemplate.status_detail_name,
      }
    }
    return null;
  }, [previousFormDependencyDetailInfo]);

  const handleStatusChange = (e) => {
    const selectedItem = e;
    if (!selectedItem) return;

    const selectedStatus = statuses.find((status) => status.value === selectedItem.value);

    const previousTemplates = [...previousFormDependencyDetailInfo.templates];
    // const prvToPrint = [...previousTemplates].map((tmplt) => ({...tmplt}));
    previousTemplates.forEach((tmplt) => {
      if (tmplt.id === template.id) {
        tmplt.status_detail_id = selectedStatus.value;
        tmplt.status_detail_name = selectedStatus.label;
      }
    });

    setPreviousFormDependencyDetailInfo({ ...previousFormDependencyDetailInfo, templates: previousTemplates });
  };

  useEffect(() => {
    if (statusSets?.length > 0) {
      let statusSet = statusSets.find((statusSet) => statusSet.id === template.status_set_id)
      let details = statusSet?.details?.details || [];
      if (details.length > 0) {
        let statusOptions = details?.map((detail) => ({
          value: detail.id,
          label: detail.name,
        }))
        
        if (statusOptions?.length > 0) {
            setStatuses(statusOptions);

        }
      }
    }
  }, [statusSets]);

  return (
    <>
      <div className={className} id={id}>
        <div className="template-name">
          <span className="name">{template.name}</span>
        </div>
        <div className="template-status">
          <span className="label">{template.status_set_name}:</span>
          <SelectBox
            className="SelectBoxStatus"
            options={statuses}
            value={statusValue}
            placeholder={translate(
              "project_settings.form_dependencies.add_form_dependency_detail_modal.select_status_placeholder"
            )}
            onChange={handleStatusChange}
          />
        </div>
      </div>
    </>
  );
};

const AddNewFormDependencyDetail = ({
  dependencySet,
  show,
  setShow,
  translate,
  refreshFormDependencyDetails,
  previousFormDependencyDetailId,
  nextHoldPointNumber,
}) => {
  const { project } = useProject();
  const [statusSets, setStatusSets] = useState([]);
  const [formTemplates, setFormTemplates] = useState([]);

  const [previousFormDependencyDetailInfo, setPreviousFormDependencyDetailInfo] = useState(null);
  const [formDependencyDetailInfo, setFormDependencyDetailInfo] = useState({
    dependent_to: previousFormDependencyDetailId || null,
    name: `Hold Point ${nextHoldPointNumber}`,
    template_ids: [],
  });

  const language = useLanguage();

  const { setRequestLoading, requestLoading } = useContext(RequestContext);

  const handlePreviousFormDependencyChange = (e) => {
    setPreviousFormDependencyDetailInfo({
      ...previousFormDependencyDetailInfo,
      [e.target.name]: e.target.value,
    });
  };

  const handleFormTemplateChange = (e) => {
    setFormDependencyDetailInfo({
      ...formDependencyDetailInfo,
      template_ids: e,
    });
  };

  const failedValidation = (required) => {
    return required.some((field) => {
      if (!field.validation) {
        confirmAlert({
          customUI: ({ onClose }) => (
            <AlertModal
              buttons={[
                {
                  label: translate("okay"),
                  onClick: onClose,
                  classNames: "btn btn-primary",
                },
              ]}
              message={field.message}
            />
          ),
        });
        return true;
      }

      return false;
    });
  };


  const handleAddFormDependency = async (e) => {
    e.preventDefault();

    const requiredFields = [
      {
        validation: !previousFormDependencyDetailInfo || (previousFormDependencyDetailInfo.templates && previousFormDependencyDetailInfo.templates.length > 0 && previousFormDependencyDetailInfo.templates.every((template) => template.status_detail_id)),
        message: translate("project_settings.form_dependencies.add_form_dependency_detail_modal.previous_form_dependency_details_required"),
      },
      {
        validation: !previousFormDependencyDetailInfo || (previousFormDependencyDetailInfo.name && previousFormDependencyDetailInfo.name.length > 0),
        message: translate("project_settings.form_dependencies.add_form_dependency_detail_modal.previous_form_dependency_detail_name_required"),
      },
      {
        validation: (formDependencyDetailInfo.template_ids && formDependencyDetailInfo.template_ids.length > 0),
        message: translate("project_settings.form_dependencies.add_form_dependency_detail_modal.form_dependency_detail_form_templates_required"),
      },
    ];

    if (failedValidation(requiredFields)) {
      return;
    }

    setRequestLoading(true);
    try {
      await API.postData(`form_dependency/${dependencySet.id}/details`, {
        ...formDependencyDetailInfo,
        template_ids: formDependencyDetailInfo.template_ids.map((template) => ({
          id: template.id,
          status_set_detail_id: null,
        })),
      });

      if (previousFormDependencyDetailId) {
        await API.putData(`form_dependency_detail/${previousFormDependencyDetailId}`, {
          name: previousFormDependencyDetailInfo?.name,
          template_ids: previousFormDependencyDetailInfo?.templates.map((template) => ({
            id: template.original_id,
            status_set_detail_id: template.status_detail_id,
          })),
        });
      }
      toastr.success("Form dependency detail added successfully");

      refreshFormDependencyDetails(); // TODO: This may only trigger one set's detail if possible
      setShow(false);
    } catch (error) {
      console.error("Catch error:", error);
      toastr.error(error?.response?.data?.message);
    } finally {
      setRequestLoading(false);
    }
  };

  const getPreviousFormDependencyDetailInfo = async () => {
    const res = await API.getData(`/form_dependency_detail/${previousFormDependencyDetailId}`);

    const data = res?.data?.data || null;
    setPreviousFormDependencyDetailInfo(data);
    if (data) {
      const statusRes = await API.getData(`/status_sets?project_id=${project.id}`);
      const statusSets = statusRes?.data?.data || [];

      const uniqueStatusSets = [...new Set(data.templates?.map((template) => template.status_set_id))];
      const filteredStatusSet = statusSets.filter((statusSet) =>
        uniqueStatusSets.includes(statusSet.id)
      ).map((statusSet) => ({
        id: statusSet.id,
        details: statusSet,
      }));

      setStatusSets(filteredStatusSet);
    }
  };

  const getFormTemplates = () => {
    const dependencyTemplateIds = dependencySet?.details?.reduce?.((acc, detail) => [...acc, ...detail.templates?.map?.((template) => template.original_id)], []) || [];

    const payload = {
      attributes: [],
      site_set_id: dependencySet?.site?.id || null,
      id_not_in: dependencyTemplateIds || [],
    };

    const url = `/form_dependency/form_templates?project_id=${project.id}&page=1&size=1000&create=true`;
    API.postData(url, payload).then(({ data: res }) => {
      let formTemplates = [];
      res.data.forEach((formTemplate) => {
        formTemplates.push({
          id: formTemplate.original_id,
          value: formTemplate.original_id.toString(),
          label: formTemplate.name,
        });
      });
      setFormTemplates(formTemplates);
    });
  };
  
  useEffect(() => {
    if (show) {
      getFormTemplates();
    }
  }, [show, dependencySet]);

  useEffect(() => {
    if (show) {
      setFormDependencyDetailInfo({
        dependent_to: previousFormDependencyDetailId || null,
        name: `Hold Point ${nextHoldPointNumber}`,
        template_ids: [],
      });
    }
  }, [show, nextHoldPointNumber, previousFormDependencyDetailId]);

  useEffect(() => {
    if (show && previousFormDependencyDetailId) {
      getPreviousFormDependencyDetailInfo();
    }
  }, [show, previousFormDependencyDetailId]);

  return (
    <Modal
      title={translate("project_settings.form_dependencies.add_form_dependency_detail_modal.title")}
      positiveButtonNode={translate("save")}
      negativeButtonNode={translate("cancel")}
      positiveButtonHandler={handleAddFormDependency}
      modalSize="lg"
      disableButtons={requestLoading}
      show={show}
      setShow={(show) => {
        setShow(show);
      }}
    >
      <div className="d-flex flex-wrap align-items-center AddDependencyDetailContainer">
        {previousFormDependencyDetailInfo && (
          <>
            <div className="PreviousContainer">
              <label className="form-label fw-bold pb-0 mb-0 text-secondary">{translate("project_settings.form_dependencies.add_form_dependency_detail_modal.previous_form_dependency_details_label")}</label>
              <div className="templates-container">
              {previousFormDependencyDetailInfo?.templates?.map((template, index) => (
                <FormTemplateItem
                  className="FormTemplateItem"
                  id={`${index}-${template.id}-${template.status_set_id}`}
                  template={template}
                  statusSets={statusSets}
                  formDependencyDetailInfo={formDependencyDetailInfo}
                  previousFormDependencyDetailInfo={previousFormDependencyDetailInfo}
                  setPreviousFormDependencyDetailInfo={setPreviousFormDependencyDetailInfo} />
              ))}
              </div>
            </div>
            <div className="col-12 mt-2 pe-3">
              <Input
                type="text"
                name="name"
                value={previousFormDependencyDetailInfo?.name || ''}
                title={translate(
                  "project_settings.form_dependencies.add_form_dependency_detail_modal.form_dependency_detail_name"
                ).toLocaleUpperCase(language)}
                placeholder={translate(
                  "project_settings.form_dependencies.add_form_dependency_detail_modal.form_dependency_detail_name_placeholder"
                )}
                onChange={handlePreviousFormDependencyChange}
              />
            </div>
          </>
        )}
        <div className="col-12 mt-4 pe-3">
          <p className="headTitle text-secondary fw-bold mb-1 ms-1">
            {translate("project_settings.form_dependencies.add_form_dependency_detail_modal.form_dependency_detail_form_templates_label")}
          </p>
          {show && (
            <SelectBox
              options={formTemplates}
              value={formDependencyDetailInfo.template_ids}
              isMulti={true}
              placeholder={translate(
                "project_settings.form_dependencies.add_form_dependency_detail_modal.form_dependency_detail_form_templates_placeholder"
              )}
              onChange={handleFormTemplateChange}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddNewFormDependencyDetail;
